import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { Button } from "../ui/button";
import api from "../../lib/api";

/**
 * PaywallModal — D7a (iter134).
 *
 * Contextual, per-trigger paywall. Copy variants live in
 * `PAYWALL_COPY` and are stitched deterministically — no LLM.
 * CTA routes to /pricing?ref={trigger} so the pricing page can
 * highlight the specific feature the user just tried to access.
 */

const PAYWALL_COPY = {
  historical_signals: {
    title: "Historical Signals · Elite",
    quote: "Coach compares every trade against your last 20 fills per symbol — Elite only.",
    features: [
      "Real historical baselines (size, leverage, hold-time)",
      "Anomaly nudges before you place the next order",
      "Session Contract breach patterns over time",
    ],
  },
  thesis_history: {
    title: "Full Thesis Archive · Elite",
    quote: "Your first three reviewed theses are free. See the rest of your written record — Elite unlocks the full archive.",
    features: [
      "Up to 50 reviewed theses per view",
      "Wins/losses filters across your full history",
      "Pattern surfacing across weeks and months",
    ],
  },
  weekly_narrative: {
    title: "Weekly Narrative Archive · Elite",
    quote: "Free keeps your last 4 weekly recaps. Elite keeps a full year plus lets you regenerate any week on demand.",
    features: [
      "52 weekly recaps kept indefinitely",
      "Regenerate any past week deterministically",
      "Milestone thread across the whole year",
    ],
  },
  pdf_export: {
    title: "PDF Export · Elite",
    quote: "Trade Doctor PDFs — Elite feature.",
    features: [
      "Printable + shareable audit reports",
      "Cross-broker aggregate PDFs",
      "Session Contract history on every export",
    ],
  },
  doctor_report_second: {
    title: "Trade Doctor · Elite",
    quote: "Your first Trade Doctor audit was free. Elite unlocks unlimited audits — regenerate any time your data changes.",
    features: [
      "Unlimited Doctor audits",
      "Regenerate after every new upload",
      "Cross-broker analysis + PDF export",
    ],
  },
};

export default function PaywallModal({ open, onOpenChange, trigger, extraQuote }) {
  // D7b polish — fire the idempotent first-exposure cohort marker
  // whenever ANY paywall modal opens, so the denominator for later
  // D7c funnel analytics counts every trigger, not just Doctor's
  // 402 path. Endpoint is at-most-once server-side via $exists guard.
  useEffect(() => {
    if (!open) return;
    api.post("/entitlements/paywall-shown").catch(() => {});
  }, [open]);
  if (!open) return null;
  const copy = PAYWALL_COPY[trigger] || PAYWALL_COPY.historical_signals;
  const close = () => onOpenChange?.(false);
  return (
    <div
      data-testid={`paywall-modal-${trigger}`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={close}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-[var(--bt-surface)] border border-[var(--bt-accent)] rounded-sm p-6 max-w-lg w-full space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[var(--bt-accent)] flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4 text-black" />
            </div>
            <h2 className="font-heading text-xl">{copy.title}</h2>
          </div>
          <button
            data-testid={`paywall-close-${trigger}`}
            onClick={close}
            className="text-[var(--bt-text-muted)] hover:text-[var(--bt-text)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <blockquote className="border-l-2 border-[var(--bt-accent)] pl-3 text-sm italic text-[var(--bt-text)]">
          {extraQuote || copy.quote}
        </blockquote>
        <ul className="space-y-1.5 text-sm text-[var(--bt-text)]">
          {copy.features.map((f) => (
            <li
              key={f}
              className="flex items-start gap-2"
              data-testid={`paywall-feature-${trigger}`}
            >
              <span className="text-[var(--bt-accent)] mt-0.5">✦</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--bt-border)]">
          <Button
            data-testid={`paywall-later-${trigger}`}
            onClick={close}
            variant="outline"
            className="h-9 rounded-sm border-[var(--bt-border)] text-xs font-mono uppercase"
          >
            Maybe later
          </Button>
          <Link to={`/pricing?ref=${encodeURIComponent(trigger)}`}>
            <Button
              data-testid={`paywall-upgrade-${trigger}`}
              className="h-9 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold text-xs font-mono uppercase"
            >
              Upgrade to Elite
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
